/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import {
  Component,
  computed,
  input,
  output,
  QueryList,
  signal,
  ViewChildren,
} from '@angular/core';
import {
  CalendarClickEvent,
  CalendarDropTask,
  CalendarTask,
  CalendarTaskHeaderGroup,
  WeekSelectionEvent,
} from '@infodata-group/inf-commons';
import { Time } from '@angular/common';
import dayjs from 'dayjs';
import { CalendarViewWeekHorizontalComponent } from '../calendar-view-week-horizontal/calendar-view-week-horizontal.component';
import { CalendarHorizontalResourcesComponent } from '../calendar-horizontal-resources/calendar-horizontal-resources.component';
import { CalendarResourceLine } from '../calendar-resource-line';
import { Telework } from '../telework';

type ResourcesFortnight = {
  week1: CalendarResourceLine[];
  week2: CalendarResourceLine[];
};

@Component({
  selector: 'infl-calendar-view-fortnight-horizontal',
  templateUrl: './calendar-view-fortnight-horizontal.component.html',
  styleUrl: './calendar-view-fortnight-horizontal.component.scss',
  standalone: true,
  imports: [
    CalendarHorizontalResourcesComponent,
    CalendarViewWeekHorizontalComponent,
  ],
})
export class CalendarViewFortnightHorizontalComponent {
  public resources = input<CalendarTaskHeaderGroup[]>();
  public partial = input(false);
  public start = input<Time>({ hours: 7, minutes: 0 });
  public endMorning = input<Time>({ hours: 12, minutes: 0 });
  public startAfternoon = input<Time>({ hours: 13, minutes: 0 });
  public end = input<Time>({ hours: 17, minutes: 30 });
  public resourceTitle = input<string>();
  public days = input<{
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
  }>();
  public hourDivCount = input(2);
  public dayWeek1 = input(new Date());
  public dragByOutside = input<CalendarDropTask<any>>();
  public tasks = input<CalendarTask[]>();
  public sectionHeight = input(40);
  public taskHeight = input(30);
  public canDrag = input(true);
  public canResize = input(true);
  public canSelect = input(true);
  public enable = input(true);
  public cellWidth = input<number | 'auto'>('auto');
  public numberSection = input<1 | 2>();
  public teleworks = input<Telework[]>(null);

  public taskUpdate = output<CalendarTask>();
  public taskCreation = output<{
    date: Date;
    group: { header: string; group: string };
  }>();
  public taskClick = output<CalendarClickEvent>();
  public taskHover = output<CalendarTask>();
  public clickOnSelection = output<WeekSelectionEvent>();
  public dblClickOnSelection = output<WeekSelectionEvent>();
  public taskDblClick = output<CalendarClickEvent>();
  public periodSelection = output<{
    component: CalendarViewWeekHorizontalComponent;
    selection: WeekSelectionEvent;
  }>();
  public hourPreselection = output<WeekSelectionEvent>();
  public taskContextMenu = output<CalendarTask>();

  public dayWeek2 = computed(() => {
    return dayjs(this.dayWeek1()).add(1, 'week').toDate();
  });

  private resourcesLines: ResourcesFortnight = {
    week1: [],
    week2: [],
  };

  public draggingTask = signal<{ task: CalendarTask; shift: number }>(null);

  @ViewChildren('viewH') views: QueryList<CalendarViewWeekHorizontalComponent>;

  public generateTasks() {
    this.onChangeDisplay();
  }

  onChangeDisplay() {
    for (const view of this.views) {
      view.onChangeDisplay();
    }
  }

  onInitResources(event: CalendarResourceLine[], weekNumber: number) {
    switch (weekNumber) {
      case 1:
        this.resourcesLines.week1 = event;
        break;
      case 2:
        this.resourcesLines.week2 = event;
        break;
    }
    if (
      this.resourcesLines.week1.length === 0 ||
      this.resourcesLines.week2.length === 0
    ) {
      return;
    }
    for (let i = 0; i < this.resourcesLines.week1.length; i++) {
      const resourceWeek1 = this.resourcesLines.week1[i];
      const resourceWeek2 = this.resourcesLines.week2[i];
      const maxHeight = Math.max(resourceWeek1.height, resourceWeek2.height);
      resourceWeek1.resource.increaseHeight(
        maxHeight * resourceWeek1.rowHeight,
      );
      resourceWeek2.resource.increaseHeight(
        maxHeight * resourceWeek2.rowHeight,
      );
    }
    this.resourcesLines.week1 = [];
    this.resourcesLines.week2 = [];
  }
}
